import { z } from "zod";
import type { DbtCauseCode, DbtCauseView, DbtTestKind } from "./dbtCauses";
import type { RunKind, RunStartedBy, RunStatus, StartRunResponse, StepStatus } from "./runs";
import { byCodeUnit } from "../sort";
import { dbtJobWarningSchema } from "../dbt/jobWarnings";

/**
 * dbt transformations (FR-62). This release stores and orders them; the dbt run itself is a
 * later phase, so a transformation is configuration only: a name, a dbt selector and the
 * transformations it waits for.
 *
 * `dependsOn` holds ids of other transformations in the SAME instance. The service checks
 * that each id exists and that a transformation does not depend on itself; a longer cycle is
 * rejected too, because the schedule editor orders the list by these edges and a cycle would
 * make the ordering undefined.
 */

/**
 * The characters a dbt node selector may contain, and its length limit.
 *
 * ONE definition, imported by both ends: `server/services/DbtJobService.ts` refuses a
 * value the editor sends, and `runner/executors/DbtJobExecutor.ts` refuses the merged `--select`
 * it is about to write into a bundle. The run-time check is the one that must exist — the dbt
 * image's own `run.py` is not in this repository, so the bundle boundary is where the contract is
 * enforced — but a charset only checked there turns a mistyped selector into a failed nightly run
 * hours later instead of a 400 in the editor, and two copies of the charset would drift.
 *
 * The charset is dbt's own selector grammar: `tag:nightly`, `+model_orders`, `path:models/x`,
 * `a,b` for intersection, a space for union, `@` and `*` for the graph and wildcard operators.
 * `MAX_DBT_SELECTOR_LENGTH` is the `dbt_selector` column's own limit.
 */
export const DBT_SELECTOR_CHARSET = /^[A-Za-z0-9_.,:+*@/ -]+$/;
export const MAX_DBT_SELECTOR_LENGTH = 500;

export const dbtJobsQuerySchema = z.object({
  enabled: z
    .enum(["true", "false"])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === "true")),
});
export type DbtJobsQuery = z.infer<typeof dbtJobsQuerySchema>;

/**
 * `runTests` is whether `dbt test` runs for this transformation after `dbt run`.
 *
 * It defaults to false on both write bodies, so a client that has never heard of the field
 * keeps the behaviour it has today, and the tenant column defaults the same way for every row
 * that existed before tenant migration 0002. A test costs warehouse time in the customer's
 * account, so opting in is a decision somebody makes rather than one a migration makes for
 * them. The runner unions the selectors of the transformations that opted in into the bundle's
 * `testSelect`, and a failed test is its own exit class, never folded into the run's.
 */
export const dbtJobViewSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  dbtSelector: z.string().nullable(),
  dependsOn: z.array(z.uuid()),
  enabled: z.boolean(),
  runTests: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** #1292: only on the answer to a create or an edit, when the save is fine and worth a look. */
  warnings: z.array(dbtJobWarningSchema).optional(),
});
export type DbtJobView = z.infer<typeof dbtJobViewSchema>;

export const createDbtJobBodySchema = z.object({
  name: z.string().min(1).max(200),
  /**
   * A dbt node selector (`tag:nightly`, `+model_orders`). Kept as text and never interpolated
   * into SQL; the runner passes it to dbt as an argument.
   */
  dbtSelector: z.string().max(MAX_DBT_SELECTOR_LENGTH).nullable().optional(),
  dependsOn: z.array(z.uuid()).max(50).default([]),
  enabled: z.boolean().default(true),
  runTests: z.boolean().default(false),
});
export type CreateDbtJobBody = z.infer<typeof createDbtJobBodySchema>;

export const updateDbtJobBodySchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    dbtSelector: z.string().max(MAX_DBT_SELECTOR_LENGTH).nullable().optional(),
    dependsOn: z.array(z.uuid()).max(50).optional(),
    enabled: z.boolean().optional(),
    runTests: z.boolean().optional(),
  })
  .refine((body) => Object.keys(body).length > 0, "Send at least one field to change.");
export type UpdateDbtJobBody = z.infer<typeof updateDbtJobBodySchema>;

/* ------------------------------------------------------------------------------------- *
 * Running transformations on demand, and reading what the last run did (session 6)
 * ------------------------------------------------------------------------------------- */

/**
 * `POST /dbt-jobs/run`: a run with NO connections and only the transform step.
 *
 * `dbtJobIds` narrows the run to some of the instance's enabled transformations; absent,
 * every enabled one runs. Either way the ids are resolved and pinned when the run is created —
 * the run has no schedule, so the step row itself carries them (`metrics.dbtJobIds`) and
 * the runner reads them back from there rather than from a schedule that does not exist.
 *
 * Every field is optional, so a request with NO body is the natural call — the button sends an
 * `Idempotency-Key` header and nothing else. Express leaves `req.body` undefined when no JSON
 * arrives, and `z.object` refuses `undefined`, so the schema defaults to the empty body.
 */
/**
 * The longest merged `--select` a bundle takes (`assertDbtSelect` in
 * `runner/executors/DbtJobExecutor.ts`). Here so the run body and the Run dialog refuse what the
 * runner would refuse. Round 6 T-29 (owner: "should we really show this to the user? can we not
 * overcome this?") raised it from 4,000. No operating-system cap applies: the selection
 * travels in the config bundle (JSON in blob storage, never on the job's command line), and the
 * image runs dbt in process through `dbtRunner` (`transform/skydata_dbt/dbt_invoke.py`), so it
 * is never an argv element or an environment variable. The pin is `run_steps.metrics` (jsonb),
 * also uncapped. 100,000 is a bound chosen to keep the bundle, the pin and the run lists
 * small: 500 chosen models of up to 199 characters each fit it.
 */
export const MAX_DBT_SELECT_LENGTH = 100_000;

/**
 * Round 6 (T3): a run of models chosen on the Models view. It runs no saved transformation, so
 * the runner names it with this id and label in the bundle's `models[]`, the step's `models` and
 * `ranDbtJobIds`, and History, the Runs list and the Models view read "Chosen models".
 */
export const CHOSEN_MODELS_ID = "chosen-models";
export const CHOSEN_MODELS_LABEL = "Chosen models";

/** A dbt model name as the manifest summary lists it: no selector operator, no space. */
export const DBT_MODEL_NAME_PATTERN = /^[A-Za-z0-9_][A-Za-z0-9_.-]*$/;
/** The most models one run of chosen models names (the run body's `models`). */
export const MAX_CHOSEN_MODELS = 500;

/**
 * The `--select` of a run of chosen models: the names joined by a space (dbt's union), each with
 * `+` before it when the models they depend on are built too. One helper for the server (which
 * pins it), the runner (which bundles it) and the dialog (which shows it and its length).
 */
export function chosenModelsSelect(models: readonly string[], buildParents: boolean): string {
  return models.map((name) => (buildParents ? `+${name}` : name)).join(" ");
}

/**
 * Whether one run takes these chosen models: at most `MAX_CHOSEN_MODELS` names and a selection
 * of at most `MAX_DBT_SELECT_LENGTH` characters, the two limits the run body refuses past. The
 * Run dialog says "Too many models chosen" past either, and the Models view's "Run selected"
 * with every model chosen becomes "Run all dbt jobs now" only past either (review r1).
 */
export function chosenModelsFitOneRun(models: readonly string[], buildParents: boolean): boolean {
  return (
    models.length <= MAX_CHOSEN_MODELS &&
    chosenModelsSelect(models, buildParents).length <= MAX_DBT_SELECT_LENGTH
  );
}

/**
 * The terms of a pinned `adhocSelect`, distinct and sorted, or null when it is not one that
 * `chosenModelsSelect` makes (each term a model name, with or without `+`). Two runs of chosen
 * models select the same models when these are equal, whatever order the names came in: the
 * duplicate check (`assertNotAlreadyQueued`, `transformQueueOf`) compares them, and a row reads
 * the select only once it passes this (no metrics value is trusted on the way out).
 */
export function chosenSelectTerms(select: unknown): string[] | null {
  if (typeof select !== "string" || select.length === 0) return null;
  if (select.length > MAX_DBT_SELECT_LENGTH) return null;
  const terms = select.split(" ");
  const valid = terms.every((term) => DBT_MODEL_NAME_PATTERN.test(term.replace(/^\+/, "")));
  return valid ? [...new Set(terms)].sort(byCodeUnit) : null;
}

/**
 * Round 7 ("Keep the dbt run list small with long chosen-model selections"): the most characters
 * of a selection one row of `GET /dbt-jobs/runs` carries. A run of chosen models may pin up to
 * `MAX_DBT_SELECT_LENGTH` characters, three times over (`select`, `testSelect`,
 * `adhocSelect`), so a page of 100 such rows reached about 20 MB; past this length a list row
 * carries a preview (`previewSelect`) and `GET /dbt-jobs/runs/:runId` the whole selection.
 */
export const RUN_LIST_SELECT_PREVIEW_LENGTH = 500;

/**
 * A selection as a list row carries it: whole when it fits `RUN_LIST_SELECT_PREVIEW_LENGTH`,
 * else its leading terms that fit, then " …" (a term is never cut, and the ellipsis is not a
 * term, so `chosenSelectTerms` refuses a preview rather than reading it as the selection).
 */
export function previewSelect(select: string): string {
  if (select.length <= RUN_LIST_SELECT_PREVIEW_LENGTH) return select;
  const cut = select.lastIndexOf(" ", RUN_LIST_SELECT_PREVIEW_LENGTH);
  const head = cut > 0 ? select.slice(0, cut) : select.slice(0, RUN_LIST_SELECT_PREVIEW_LENGTH);
  return `${head} …`;
}

/**
 * Round 7: a digest of a chosen selection's terms (`chosenSelectTerms`, distinct and sorted), so
 * the Run dialog can tell a duplicate from a list row that carries only a preview. Synchronous,
 * one function for the server (which sends it) and the browser (which compares it): the term
 * count and a 53-bit string hash (cyrb53). It only steers a hint; the server's own duplicate
 * check (`assertNotAlreadyQueued`) compares the terms themselves.
 */
export function selectTermsDigest(terms: readonly string[]): string {
  const text = terms.join(" ");
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const hash = 4294967296 * (2097151 & h2) + (h1 >>> 0);
  return `${terms.length}-${hash.toString(16)}`;
}

export const runDbtJobsBodySchema = z
  .object({
    dbtJobIds: z.array(z.uuid()).min(1).max(50).optional(),
    /**
     * Round 6 (T3): dbt model names from the last manifest, instead of `dbtJobIds`. Each
     * is a plain name (a selector operator would widen the run past what was chosen); the
     * server checks each against the last run's model list and refuses an unknown one by name.
     */
    models: z
      .array(
        z
          .string()
          .min(1)
          .max(MAX_DBT_SELECTOR_LENGTH - 1)
          .regex(DBT_MODEL_NAME_PATTERN, "A model is named as dbt names it, with no selector."),
      )
      .min(1)
      .max(MAX_CHOSEN_MODELS)
      .refine((names) => new Set(names).size === names.length, "A model is chosen once.")
      .optional(),
    /** With `models`: also build the models they depend on (`+name`). Off by default. */
    buildParents: z.boolean().optional(),
    /** With `models`: run the tests on what the run builds. On by default. */
    runTests: z.boolean().optional(),
  })
  .superRefine((body, issue) => {
    if (body.models && body.dbtJobIds) {
      issue.addIssue({
        code: "custom",
        path: ["models"],
        message: "Send dbtJobIds or models, not both.",
      });
    }
    if (!body.models && (body.buildParents !== undefined || body.runTests !== undefined)) {
      issue.addIssue({
        code: "custom",
        path: [body.buildParents !== undefined ? "buildParents" : "runTests"],
        message: "buildParents and runTests apply to a run of chosen models only.",
      });
    }
    if (
      body.models &&
      chosenModelsSelect(body.models, body.buildParents === true).length > MAX_DBT_SELECT_LENGTH
    ) {
      issue.addIssue({
        code: "custom",
        path: ["models"],
        message: `The chosen models make a dbt selection longer than ${MAX_DBT_SELECT_LENGTH.toLocaleString("en-US")} characters. Choose fewer models, or run a dbt job that selects them by tag.`,
      });
    }
  })
  .transform((body) => {
    if (body.models) {
      return {
        models: body.models,
        buildParents: body.buildParents ?? false,
        runTests: body.runTests ?? true,
      };
    }
    return body.dbtJobIds ? { dbtJobIds: body.dbtJobIds } : {};
  })
  .default({});
export type RunDbtJobsBody = z.input<typeof runDbtJobsBodySchema>;

/** The 202 body of `POST /dbt-jobs/run`: `POST /runs`'s, plus what was pinned. */
export interface StartTransformRunResponse extends StartRunResponse {
  /** Always empty: a transform-only run covers no connection. */
  dataflowIds: string[];
  /** The enabled transformations the run was created with, in name order; empty for chosen models. */
  dbtJobIds: string[];
  /** Round 6 (T3): the chosen models the run was created with, as sent; absent otherwise. */
  models?: string[];
}

export const dbtJobRunsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  /** Round 7: the keyset cursor of the previous page (`meta.nextCursor`), opaque. */
  cursor: z.string().max(512).optional(),
});
export type DbtJobRunsQuery = z.infer<typeof dbtJobRunsQuerySchema>;

/**
 * The phases the dbt container can be in, mirroring `PHASES` in `transform/skydata_dbt/exits.py`
 * and `transform/exit-codes.json` (Session 11: moved here from `runner/executors/DbtJobExecutor.ts`,
 * which re-exports both, so `shared/` never imports runner code and the client can read them).
 *
 * A closed set for the same reason the exit classes are one: `phase` and `phases` reach
 * `run_steps.metrics` and the screens, and the summary is written by a program in another
 * language (SEC-17). `DbtJobExecutor.mapping.test.ts` checks this list against `exit-codes.json`.
 */
export const DBT_PHASES = [
  "config",
  "auth",
  "guard",
  "clone",
  "deps",
  // Round 7: entered only when the schedule checks source freshness (`sourceFreshness`).
  "freshness",
  "run",
  "test",
  "rowcounts",
  "artifacts",
] as const;
export type DbtPhase = (typeof DBT_PHASES)[number];

/** How a phase the run entered ended (`dbt-summary/1.2.0`). */
export const DBT_PHASE_OUTCOMES = ["ok", "failed", "skipped"] as const;
export type DbtPhaseOutcome = (typeof DBT_PHASE_OUTCOMES)[number];

/** One phase of a dbt run, as `dbt-summary/1.2.0` times it: a closed name, seconds, an outcome. */
export interface DbtPhaseTiming {
  name: DbtPhase;
  /** Seconds, rounded to 0.1. */
  seconds: number;
  outcome: DbtPhaseOutcome;
}

const PHASE_NAMES = new Set<string>(DBT_PHASES);
const PHASE_OUTCOMES = new Set<string>(DBT_PHASE_OUTCOMES);

/**
 * The one rule for `phases`, on the way in (`parseDbtSummary`) and on the way out
 * (`toDbtJobRunRow`). An array of at most `DBT_PHASES.length` entries; an entry is kept
 * only when its name is a known phase not seen before, its seconds a finite number >= 0 (rounded
 * to 0.1) and its outcome a known one. Anything that is not an array (absent, a string, an
 * object) or is longer than the phase list is null: a document of that shape is not one this
 * build understands, and nothing of it is copied along. A non-empty array with no readable entry
 * is null too; only an empty array is [] ("no phase entered").
 */
export function parseDbtPhaseTimings(value: unknown): DbtPhaseTiming[] | null {
  if (!Array.isArray(value) || value.length > DBT_PHASES.length) return null;
  const seen = new Set<string>();
  const phases: DbtPhaseTiming[] = [];
  for (const entry of value) {
    if (typeof entry !== "object" || entry === null || Array.isArray(entry)) continue;
    const row = entry as Record<string, unknown>;
    const { name, seconds, outcome } = row;
    if (typeof name !== "string" || !PHASE_NAMES.has(name) || seen.has(name)) continue;
    if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds < 0) continue;
    if (typeof outcome !== "string" || !PHASE_OUTCOMES.has(outcome)) continue;
    seen.add(name);
    phases.push({
      name: name as DbtPhase,
      seconds: Math.round(seconds * 10) / 10,
      outcome: outcome as DbtPhaseOutcome,
    });
  }
  // Entries were given and none was readable: that is "not recorded", not "no phase entered".
  if (phases.length === 0 && value.length > 0) return null;
  return phases;
}

/** A git commit as `dbt-summary/1.2.0` records it: exactly 40 lowercase hex characters. */
const DBT_COMMIT = /^[0-9a-f]{40}$/;

/** The checked-out commit, or null for anything that is not 40 lowercase hex characters. */
export function parseDbtCommit(value: unknown): string | null {
  return typeof value === "string" && DBT_COMMIT.test(value) ? value : null;
}

/** Per-phase node counts, as the dbt image's summary reports them. Counts only (SEC-17). */
export interface TransformationNodeCounts {
  total: number;
  succeeded: number;
  failed: number;
  skipped: number;
}

/**
 * `GET /dbt-jobs/runs`: the schedule-level transform step of each recent run, newest
 * first, with what the runner recorded on the step: the counts are the poll step's own
 * (`modelsReported`, `modelsFailed`, `testsFailed`, `testsSucceeded`, `testsTotal`) and are null
 * for a step that never launched dbt. Only a finished step polled by an older runner (no
 * `testsTotal` on the row) costs a read of its summary blob, once per process.
 */
export interface DbtJobRunRow {
  runId: string;
  kind: RunKind;
  runStatus: RunStatus;
  triggeredBy: string | null;
  /**
   * True when the run has no connection steps — `POST /dbt-jobs/run` always, and a
   * scheduled run whose schedule names transformations and no connections.
   */
  transformOnly: boolean;
  stepStatus: StepStatus;
  attempt: number;
  /** A sentence for the operator, never dbt's own message (SEC-17). */
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  durationSeconds: number | null;
  /** `ok`, `config`, `auth`, `clone`, `deps`, `compile`, `run`, `test`, `infra`, `timeout`, … */
  exitClass: string | null;
  /**
   * #1674 (review of PR #1689): on a step that failed at the checkout only, whether the ref it
   * tried (`metrics.dbtRef`) is the ref the binding names now. False once the branch was changed
   * after the failure, so the Transformations strip never pairs the new branch with "Could not
   * clone". A boolean for every reader: the ref itself stays `instance.binding.read` (#1362).
   * Absent when it is not known (a step from before #1664, or the binding could not be read).
   */
  checkoutRefCurrent?: boolean;
  phase: string | null;
  /** The `TRANSFORM_SKIP_REASONS` token when the step was `skipped`, else null. */
  skippedReason: string | null;
  imageTag: string | null;
  select: string | null;
  testSelect: string | null;
  /**
   * #1083: whether this pass asked dbt for tests: true, false (Run tests off on every dbt job of
   * the run), or null (dbt never started for this pass, or a row from before the field).
   * `shared/runs/testsRequested.ts`.
   */
  testsRequested: boolean | null;
  /**
   * The transformations the step resolved, by name, in the order they ran. A run of chosen models
   * (round 6, T3) reads `CHOSEN_MODELS_LABEL`, from the moment it is queued.
   */
  dbtJobNames: string[];
  /**
   * Round 6 (T3): the models a run of chosen models names, as chosen; null for any other run.
   * Optional on the wire type; the server always sends the key.
   */
  chosenModels?: string[] | null;
  /**
   * Round 6 (T3 with T1): the `--select` a run of chosen models pinned ("+a +b" with Build
   * parents), so a screen can say what a queued run will build and the Run dialog can tell a
   * duplicate. Present only on a run of chosen models.
   */
  adhocSelect?: string;
  /**
   * Round 7: a digest of `adhocSelect`'s terms (`selectTermsDigest`), beside it on a run of chosen
   * models, so the Run dialog's duplicate check holds when the list carries only a preview.
   */
  adhocSelectDigest?: string;
  /** Round 7: how many models a run of chosen models names; on every read beside `chosenModels`. */
  chosenModelCount?: number;
  /**
   * Round 7: on `GET /dbt-jobs/runs` only, when a selection was longer than
   * `RUN_LIST_SELECT_PREVIEW_LENGTH`: `select`, `testSelect` and `adhocSelect` are previews
   * (`previewSelect`), `chosenModels` is absent (`chosenModelCount` stays), and
   * `GET /dbt-jobs/runs/:runId` answers the whole row.
   */
  selectionShortened?: true;
  /** Round 7: the whole `select`'s length, on a shortened list row. */
  selectLength?: number;
  modelsReported: number | null;
  modelsFailed: number | null;
  /**
   * Round 6, T5: the models that succeeded, were skipped because a model they depend on failed,
   * and were reported in any other state; with `modelsFailed` they add up to `modelsReported`.
   * Present only on a step polled by a runner that writes them; a row without them keeps the old
   * arithmetic (built = reported minus failed, `dbtModelCountsOf`).
   */
  modelsSucceeded?: number | null;
  modelsSkipped?: number | null;
  modelsNotRun?: number | null;
  /**
   * Round 6, T1: the run this step waits for while another dbt run holds the instance's one slot
   * ("Queued: waiting for run {id}"). Present only while the step is waiting.
   */
  queuedBehindRunId?: string;
  /**
   * Round 6, T1: the transformation ids a schedule-less run pinned, for the Run dialog's duplicate
   * check. Present only on a transform-only run that pinned some.
   */
  pinnedDbtJobIds?: string[];
  /**
   * W6: the whole run's duration (`runs.started_at` to `finished_at`), beside the dbt step's own
   * `durationSeconds`; screens that show both label them "Run" and "dbt step". Null while the run
   * is open; absent where the read does not select the run's times.
   */
  runDurationSeconds?: number | null;
  /**
   * B-30: when the run (re)started, ISO. A retried step has no `startedAt` until its new pass
   * begins; the list orders and labels it by `startedAt ?? runStartedAt ?? createdAt`. Absent
   * where the read does not select the run's times.
   */
  runStartedAt?: string | null;
  testsFailed: number | null;
  /**
   * How many tests passed, and how many ran in all: the poll step's copy of the image's summary
   * (`tests.succeeded` / `tests.total`), or for a step polled by an older runner the summary blob
   * itself. 0 when dbt ran and no test did (the image always writes `tests`); null while the step
   * is still running, for a step that never launched dbt, and when an old step's summary could
   * not be read. `testsTotal` includes skipped tests (total − succeeded − failed).
   */
  testsSucceeded: number | null;
  testsTotal: number | null;
  /**
   * S7: the run's primary classified cause (the first failed model, else the first failed test,
   * else the invocation's, else the failed phase), with the server's label and sentence. Present
   * only when the image classified one. The Runs tab shows the LABEL only: `error` already ends
   * with the sentence for a non-phase cause.
   */
  cause?: DbtCauseView;
  /** S7: how many node causes of each code the run had (re-validated on read). */
  causeCounts?: Partial<Record<DbtCauseCode, number>>;
  /**
   * Session 11 (D3): who started the run. Sent by `GET /dbt-jobs/runs`; absent when the
   * users read failed for the page, on a transformation's own history and on the graph's run.
   */
  startedBy?: RunStartedBy;
  /**
   * Session 11 (ruling §8.7): how many of the current pass's tests ended `warn`. `GET
   * /dbt-jobs/runs` always sends the key: null for a step with no test rows. The image
   * counts a warn as succeeded, so passed = testsSucceeded − testsWarned.
   */
  testsWarned?: number | null;
  /**
   * Session 11 (`dbt-summary/1.2.0`): each phase the run entered, in order, with seconds and an
   * outcome; re-validated on the way out. Null for a step whose summary predates 1.2.0.
   */
  phases?: DbtPhaseTiming[] | null;
  /**
   * Session 11 (`dbt-summary/1.2.0`): the checked-out commit, 40 lowercase hex. Null before 1.2.0
   * and for the builtin sample project, which is copied, not cloned.
   */
  commit?: string | null;
}

/** One dbt node of the last run's manifest summary, with what the run did to it. */
export interface TransformationGraphNode {
  /** dbt's `unique_id`, e.g. `model.skydata_saas_test.stg_customers`. */
  uniqueId: string;
  /** The last segment of the unique id: what the canvas labels the node with. */
  name: string;
  /** `model`, `source`, `seed` or `snapshot`; tests are in `tests`, everything else is dropped. */
  resourceType: string;
  materialized: string | null;
  /** The warehouse relation, an identifier; null for a node that has none. */
  relationName: string | null;
  /** Unique ids of the nodes this one selects from, restricted to nodes in this graph. */
  dependsOn: string[];
  /** dbt's status for the node in the last run (`success`, `error`, `skipped`…); null when it was not run. */
  status: string | null;
  failed: boolean | null;
  rowsAffected: number | null;
  executionTimeSeconds: number | null;
  /**
   * The ids of the run's transformations whose selector selects this node, in the run's order.
   * See `TransformationGraph.selection` for how each one was worked out.
   */
  selectedBy: string[];
  /**
   * False when at least one of the run's transformations could not be evaluated (a selector
   * form outside the supported subset on `DbtJobSelection`, or no bundle to read the
   * selectors from): then
   * `selectedBy` may be missing that transformation, and a client must not read "not listed"
   * as "not selected".
   */
  selectedByKnown: boolean;
  /**
   * Round 7: the ids of the instance's ENABLED saved dbt jobs that select this node in this
   * project, in name order, whatever the last run was (`TransformationGraph.savedSelection` says
   * how each was worked out). The Models view's "dbt job" column reads this; `selectedBy` is
   * the run's own choice. Absent when there is no manifest summary to evaluate against.
   */
  savedBy?: string[];
  /** Round 7: false when a saved dbt job could not be evaluated, so "not listed" is not "not selected". */
  savedByKnown?: boolean;
  /** S7: the node's classified cause in the graph's run, when it did not succeed. */
  cause?: DbtCauseView;
  /**
   * Session 11 (contract §6.1): the node's project-relative file (`original_file_path`, the dbt
   * path rule) and its dbt tags (each matching the summary's tag pattern; others dropped). Null
   * when the manifest summary predates them or the value failed its rule. Optional on the wire
   * type because a graph built from an older summary, and the older fixtures, lack them; the
   * server always sends the keys.
   */
  path?: string | null;
  tags?: string[] | null;
  /**
   * Session 11: the warehouse schema the node is built into, from its validated `relationName`
   * (`schemaOfRelation`, one rule per destination type); null when the relation is missing or
   * fails the rule. The Models view groups by it (the "bronze to silver" layer).
   */
  schema?: string | null;
  /**
   * Session 11 (ratification 3): the node's newest `DBT_MODEL` outcome across every run of the
   * instance, of any pass (a superseded pass's row counts when it is the newest: it was a real
   * build at that time), independent of the run the manifest came from. Null when no run has
   * built it ("Not built yet").
   */
  lastBuilt?: TransformationNodeOutcome | null;
  /** Session 11: when the node last built successfully (ISO), across every run; null when never. */
  lastSucceededAt?: string | null;
}

/**
 * Session 11: one node's outcome in one run, from one `DBT_MODEL` row of that run, of any pass
 * (`lastBuilt` takes the newest such row across every run, a superseded pass's included).
 * Identifiers, closed words, counts and seconds only (SEC-17).
 */
export interface TransformationNodeOutcome {
  runId: string;
  /** When the outcome was recorded (the event's time), ISO. */
  at: string;
  /** dbt's status, in the closed `DBT_RESULT_STATUSES` set (else `unknown`). */
  status: string;
  rowsAffected: number | null;
  executionTimeSeconds: number | null;
  cause?: DbtCauseView;
}

/** Session 11: one test's outcome in one run, from that run's current-pass `DBT_TEST` row. */
export interface TransformationTestOutcome {
  runId: string;
  at: string;
  /** `pass`, `fail`, `error`, `warn`, `skipped` (the closed set; else `unknown`). */
  status: string;
  /** Rows (or values) the test found; a count, never a value. */
  failures: number | null;
  cause?: DbtCauseView;
}

/**
 * Session 11 (`dbt-manifest-summary/1.3.0`, round-2 critic ruling "Disabled nodes"): a model,
 * seed or snapshot the project disables (`enabled: false`). Never in `nodes`, `edges`, `tests`
 * or any selection; only the Models view reads it, greyed, "Disabled in dbt". Each field is
 * constrained as the node fields are.
 */
export interface TransformationGraphDisabledNode {
  uniqueId: string;
  name: string;
  resourceType: string;
  materialized: string | null;
  path: string | null;
  tags: string[] | null;
  schema: string | null;
}

/**
 * How one transformation of the graph's run maps onto the graph's nodes.
 *
 * The supported subset of dbt's selector grammar — the forms the web evaluates against the
 * manifest summary (`server/services/transformationSelectors.ts`, API-STANDARD §7b). Anything
 * else (`a,b` intersection, `2+model` depth, `*` wildcards, `source:`, `config.`, `fqn:`, a bare
 * value that matches nothing, a tag the summary cannot carry) is `known: false`.
 *
 * - `name` — dbt's one-part fqn match: the model, seed or snapshot of that name (a versioned
 *   model by its unversioned name), every node of the package of that name, and everything under
 *   the first-level folder of that name (`orders` = `models/orders.sql` + `models/orders/**`).
 *   Needs `original_file_path` on every such node, so a 1.0.0 summary answers `known: false`;
 * - `+x` ancestors, `x+` descendants, `+x+` both, `@x` descendants and their ancestors, over any
 *   of the methods here;
 * - `tag:x` — nodes carrying the tag (needs `tags` in the manifest summary; a tag outside the
 *   image's `_TAG_RE` could have been dropped from it, so it is `known: false`);
 * - `path:x`, or a bare value with a `/` — nodes whose file is `x` or under `x/` (needs
 *   `original_file_path` on every node; the image writes "" for a path it dropped, read as
 *   missing);
 * - a space is a union of the above.
 */
export interface DbtJobSelection {
  dbtJobId: string;
  name: string;
  /** The selector the run used for it (from the run's bundle, not the row as it is now). */
  selector: string | null;
  /**
   * `selector`: evaluated against the manifest summary. `sole`: the only transformation of the
   * run, so it selected exactly the nodes dbt ran. `null`: not known (`known: false`).
   */
  basis: "selector" | "sole" | null;
  known: boolean;
  /** The first term that could not be evaluated, and why; null when `known`. */
  unsupported: string | null;
}

/** #1074: how a dbt job's own newest launched transform step finished. */
export type DbtJobOwnRunStatus = "succeeded" | "failed" | "cancelled";

/** Walk 2026-10-02, D4: a saved transformation attributed by its own last run. */
export interface DbtJobOwnRun {
  dbtJobId: string;
  name: string;
  /** The run it is attributed from: its own newest launched, finished transform step. */
  runId: string;
  basis: "selector" | "sole" | null;
  /**
   * Whether its selector was evaluated (the nodes it selects carry its id). False when the
   * selector could not be evaluated, or there was no project or bundle to evaluate it against:
   * the entry then says only how the run went (`status`, `finishedAt`), #1074.
   */
  known: boolean;
  /**
   * #1074: the step's own status and finish time, from the run record, so a run that failed
   * before dbt reported a node still reads Failed. Optional on the wire type: older fixtures
   * lack them.
   */
  status?: DbtJobOwnRunStatus;
  finishedAt?: string | null;
  /**
   * #1074 review: whether that step launched dbt (its `configPath` under its `runPrefix`). False
   * for a hand-started run that failed before the bundle was written: nothing of it ran in dbt.
   */
  launched?: boolean;
  /** #1074 review: the step's exit class (`run`, `test`, `clone`, ...); null when it has none. */
  exitClass?: string | null;
  /**
   * #1074 review: whether that run asked dbt for tests (`metrics.testSelect` set); null when the
   * step never recorded it (it failed before launch, or predates the field).
   */
  testsAsked?: boolean | null;
  /** #1074 review: how many dbt jobs that step ran (or was pinned to); 1 when it ran this one alone. */
  jobsInRun?: number;
}

export interface TransformationGraphEdge {
  /** The dependency. */
  from: string;
  /** The dependent. */
  to: string;
}

/** One dbt test of the last run, with its result when `dbt test` ran. */
export interface TransformationGraphTest {
  uniqueId: string;
  name: string;
  /** Unique ids of the nodes the test covers, restricted to nodes in this graph. */
  dependsOn: string[];
  /** dbt's status (`pass`, `fail`, `error`, `warn`, `skipped`); null when tests did not run. */
  status: string | null;
  /** How many rows the test found, for a failed test; null otherwise. A count, never a value. */
  failures: number | null;
  executionTimeSeconds: number | null;
  /** S7: the classified cause of a failed, warned or errored test. */
  cause?: DbtCauseView;
  /** S7: the test's kind, from the 1.2.0 manifest summary (or its test result row). */
  kind?: DbtTestKind;
  /** S7: the tested column, when it is a plain identifier (an expression is never kept). */
  column?: string;
  /**
   * Session 11: the test's latest current-pass `DBT_TEST` outcome over the last 30 finished
   * transform steps (a row from an older run than the graph's says so on screen); null when none
   * of them ran it. Optional on the wire type; the server always sends the key.
   */
  lastResult?: TransformationTestOutcome | null;
  /** Session 11: the test's configured severity from the manifest summary; null when unknown. */
  severity?: "error" | "warn" | null;
  /**
   * Session 11 (review S2): the project-relative file the test is defined in (its schema `.yml`
   * for a generic test, its `.sql` for a singular one), under the node path rule; null when the
   * summary has none or it fails the rule. The Tests view's "View source" link is built from it.
   */
  path?: string | null;
}

/** The transform step of the run the graph was built from. */
export interface TransformationGraphRun extends DbtJobRunRow {
  /** From the image's summary when it was readable; the poll step's counts otherwise. */
  models: TransformationNodeCounts | null;
  tests: TransformationNodeCounts | null;
  dbtVersion: string | null;
}

export interface TransformationTarget {
  destinationType: "snowflake" | "databricks";
  /**
   * Round 6 (W12): whether the warehouse is bound (a Snowflake account, or a Databricks SQL
   * warehouse id), so a screen never shows the binding's defaults as if they were configured. A
   * boolean, never the account. Optional on the wire type; the server always sends it.
   */
  accountBound?: boolean;
  database: string | null;
  schema: string | null;
  warehouse: string | null;
  // No `role` (ADR 0011, 2026-10-08): the loader's role is withheld below TenantAdmin, and this
  // row is read by every role (`dbt_jobs.read`). Settings > Destination shows it to a TenantAdmin.
}

/**
 * `GET /dbt-jobs/graph`.
 *
 * Built from the LAST run whose transform step launched dbt: the run's `_results/` artifacts in
 * the staging container (identifiers only — the manifest summary, the image's summary and the
 * test results) plus that run's `DBT_MODEL` rows in `run_events`. `run` is null when no run has
 * launched dbt yet; `artifacts` says which documents were readable, so a canvas can tell "no
 * tests ran" from "the test results could not be read".
 */
export interface TransformationGraph {
  run: TransformationGraphRun | null;
  /** What the next run will clone and run, from the binding as it is now. */
  bundle: {
    repoBound: boolean;
    /**
     * The repository's address. #1306: answered only to a holder of `instance.binding.read`
     * (Instance admin and above); below it null, with `repoUrlWithheld` true.
     */
    repoUrl: string | null;
    /** #1306: true when a repository may be set but its address is not this reader's to see. */
    repoUrlWithheld?: true;
    /**
     * The branch, tag or commit. #1362: like the address, answered only to a holder of
     * `instance.binding.read`; below it null, with `detailsWithheld` true.
     */
    ref: string | null;
    /** The project folder. #1362: null below `instance.binding.read` (see `detailsWithheld`). */
    subdir: string | null;
    /**
     * #1362: true when this reader holds no `instance.binding.read`, so `ref`, `subdir` and the
     * target's warehouse are not sent. Never set for an Instance admin.
     */
    detailsWithheld?: true;
    /**
     * The image tag the last launched run used, as the runner recorded it; null until a run has
     * launched dbt. The web never reports a tag of its own: the image is runner configuration.
     */
    imageTag: string | null;
    /**
     * Where dbt builds, from the instance binding as it is now: names only, never a credential,
     * an account locator or a user. Null when the instance has no binding. For Databricks only
     * `warehouse` (the SQL warehouse id) is set; dbt on Databricks does not run yet (FR-63).
     */
    target: TransformationTarget | null;
  };
  /**
   * The run's transformations and how each maps onto `nodes[].selectedBy`, in the run's order.
   * Null when there is no run or the run's bundle could not be read.
   */
  selection: DbtJobSelection[] | null;
  /** From the manifest summary; null when it was not readable. */
  project: {
    name: string | null;
    dbtVersion: string | null;
    nodeCount: number;
    /**
     * Session 11 (walk-back, N10): the run whose manifest summary the project facts and `nodes`
     * come from. It is `run.runId` unless the newest launched step had no readable manifest
     * (it stopped before dbt read the project), when it names the older run the list is from.
     */
    fromRunId?: string | null;
    /**
     * Session 11 (review S3): the models, seeds, snapshots, sources and tests the manifest names
     * that are not listed because their unique_id fails the rule (a character the screen cannot
     * show). `nodeCount` also counts hooks, analyses and exposures, which are never listed, so
     * the Models footnote reads this count, never a difference of `nodeCount`.
     */
    unlistedCount?: number;
  } | null;
  nodes: TransformationGraphNode[];
  /**
   * Session 11 (`dbt-manifest-summary/1.3.0`): the models, seeds and snapshots the project
   * disables. Empty for an older summary (it could not list them) and when there is no manifest.
   * Only the Models view reads this; every other consumer reads `nodes`, which never holds one.
   */
  disabledNodes?: TransformationGraphDisabledNode[];
  /**
   * Walk 2026-10-02, D4: each saved dbt job (enabled or not, #1074) the graph's run did not name, with
   * the run it is attributed from (its own newest launched, finished step); its id is in
   * `nodes[].selectedBy` for the nodes its selector selects in this project when `known`.
   * Listed whenever it has such a step, also with no bundle or project to evaluate it against
   * (`known: false`, #1074). Optional on the wire type: older servers and fixtures lack it.
   */
  ownRuns?: DbtJobOwnRun[];
  /**
   * Round 7: every enabled saved dbt job evaluated against this project (`nodes[].savedBy`), in
   * name order, whatever the last run selected: by its saved selector (`basis: "selector"`), or,
   * for a selector outside the supported forms, by what a run of it alone built (`sole`, the
   * graph's run or its own last run). Null when no manifest summary was readable. Optional on the
   * wire type: older fixtures lack it, and a client then reads `selectedBy` as before.
   */
  savedSelection?: DbtJobSelection[] | null;
  /**
   * Round 6 T-29: every dbt tag the listed nodes carry, once each and sorted, from the same
   * manifest summary as `nodes`, for the dbt job drawer's Tag list (`tag:<name>`). Empty when
   * no run has listed the project yet, or its summary predates tags. Optional on the wire type:
   * older fixtures lack it.
   */
  tags?: string[];
  edges: TransformationGraphEdge[];
  tests: TransformationGraphTest[];
  artifacts: {
    manifest: boolean;
    summary: boolean;
    testResults: boolean;
    /** The run's bundle (`_config/dbt.json`), read for its `models[]` selectors only. */
    bundle: boolean;
    /** The error class when storage could not be read at all; null when it could. */
    storageError: string | null;
  };
}

/** A node's source SQL, as the image's source bundle stores it (`dbt-node-source/1.0.0`). */
export interface TransformationNodeSourceText {
  sql: string;
  /** UTF-8 bytes of `sql` as served (after the image's 128 KB cut). */
  bytes: number;
  /** True when the image cut the text at 128 KB and appended its marker. */
  truncated: boolean;
}

/**
 * `GET /dbt-jobs/runs/:runId/nodes/:nodeId/source`, the Query dialog.
 *
 * The SOURCE SQL of one model or test as the run read it: the file as written in the customer's
 * repository (dbt's `raw_code`), never compiled or executed SQL, which a compile-time macro can
 * fill with values read from the warehouse (owner decision 2026-10-14,
 * `docs/adr/0011-amendment-2026-10-14-no-compiled-sql.md`). Read from the run's own source bundle
 * in the instance's artifact store, served only by this audited read (the audit row holds
 * identifiers and a byte count, never the text) with `Cache-Control: no-store`.
 */
export interface TransformationNodeSource {
  runId: string;
  nodeId: string;
  /** The node's file in the repository (`original_file_path`), or null. */
  path: string | null;
  /** The source as written; null for a unit test (YAML) or a node the run had no source for. */
  source: TransformationNodeSourceText | null;
  /**
   * For a failed or warned node, one line standing for dbt's message, reduced to the ADR 0011
   * allow-list (`dbtMessageLine`, by the image and again by the server): a closed sentence, the
   * code, SQLSTATE and query id, and the bundle's own node names; null otherwise.
   */
  message: string | null;
  /** True when the run's bundle passed its 4 MB cap before this node, so its source was not kept. */
  omitted: boolean;
  /** True when the bundle omitted any node for the 4 MB cap. */
  bundleTruncated: boolean;
  /**
   * For a generic data test (`not_null`, `unique`, `accepted_values`, `relationships` or a
   * package's), the test's name and column: its SQL is dbt's generated call of the test macro,
   * not a file in the repository, so `source` is null and the dialog says where it is defined
   * (`path`, the YAML file). Null for any other node (review on #1027).
   */
  generic: TransformationGenericTest | null;
}

/** A generic data test as the source bundle names it (`test_metadata` in dbt's manifest). */
export interface TransformationGenericTest {
  /** The test's name, with its package when it has one (`dbt_utils.expression_is_true`). */
  test: string;
  /** The column it tests, or null for a model-level test. */
  column: string | null;
}
